"use client";

import { useCallback, useMemo, useState } from "react";
import Link from "@/components/locale-link";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboardOrThrow } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";

type Style = "apa" | "mla";

const WHITESPACE_RE = /\s+/;

function countWords(text: string): number {
  return text
    .trim()
    .split(WHITESPACE_RE)
    .filter((w) => w.length > 0).length;
}

/** English strings. A translated content file overrides these through `ui`. */
export const ANNOTATED_BIBLIOGRAPHY_MAKER_STRINGS = {
  citationStyle: "Citation Style",
  apa7thEdition: "APA (7th Edition)",
  mla9thEdition: "MLA (9th Edition)",
  citationReferenceEntry: "Citation (Reference Entry)",
  pasteOrTypeEntry:
    "Paste or type your full reference entry. Use our {link} to create one.",
  citationGenerator: "Citation Generator",
  annotation: "Annotation",
  annotationRange: "{count} / 150–200 words",
  summary: "Summary",
  whatIsTheSourceAbout:
    "What is the source about? Summarize the main argument or findings.",
  thisArticleExamines: "This article examines...",
  wordsCount: "one:{count} word|other:{count} words",
  assessment: "Assessment",
  isTheSourceReliableIs:
    "Is the source reliable? Is the author credible? What are its strengths and weaknesses?",
  theAuthorIsALeading: "The author is a leading researcher in...",
  reflection: "Reflection",
  howDoesThisSourceRelate:
    "How does this source relate to your research? How will you use it?",
  thisSourceWillBeUseful:
    "This source will be useful for my research because...",
  lengthGuideline: "Length Guideline",
  lengthGuidelineText:
    "Most annotated bibliographies require {range} per annotation. Your current annotation is {count}.",
  n150200Words2: "150–200 words",
  formattedPreview: "Formatted Preview",
  copied: "Copied!",
  copy: "Copy",
  apaHangingIndentOnThe:
    "APA: Hanging indent on the citation, annotation indented 0.5 inch.",
  mlaHangingIndentOnThe:
    "MLA: Hanging indent on the citation, annotation indented 1 inch.",
  apaCitationPlaceholder:
    "Smith, J. (2024). Title of the article. Journal Name, 10(2), 45-60. https://doi.org/10.xxxx",
  mlaCitationPlaceholder:
    'Smith, John. "Title of the Article." Journal Name, vol. 10, no. 2, 2024, pp. 45-60.',
  yourCitationHere: "[Your citation here]",
  yourAnnotationHere: "[Your annotation here]",
} as const;

export default function AnnotatedBibliographyMakerWidget() {
  const [citation, setCitation] = useState("");
  const [style, setStyle] = useState<Style>("apa");
  const [summary, setSummary] = useState("");
  const [assessment, setAssessment] = useState("");
  const [reflection, setReflection] = useState("");
  const [copied, setCopied] = useState(false);

  const annotationWordCount = useMemo(() => {
    return (
      countWords(summary) + countWords(assessment) + countWords(reflection)
    );
  }, [summary, assessment, reflection]);

  const annotation = useMemo(() => {
    const parts: string[] = [];
    if (summary.trim()) {
      parts.push(summary.trim());
    }
    if (assessment.trim()) {
      parts.push(assessment.trim());
    }
    if (reflection.trim()) {
      parts.push(reflection.trim());
    }
    return parts.join(" ");
  }, [summary, assessment, reflection]);

  const strings = useToolUi(ANNOTATED_BIBLIOGRAPHY_MAKER_STRINGS);
  const locale = useToolLocale();

  const preview = useMemo(() => {
    if (!(citation.trim() || annotation)) {
      return "";
    }

    const citText = citation.trim() || strings.yourCitationHere;
    const annText = annotation || strings.yourAnnotationHere;

    if (style === "apa") {
      return `${citText}\n\n    ${annText}`;
    }
    // MLA: annotation is a new paragraph, indented
    return `${citText}\n\n        ${annText}`;
  }, [citation, annotation, style, strings]);

  const handleCopy = useCallback(async () => {
    if (!preview) {
      return;
    }
    try {
      await writeToClipboardOrThrow(preview);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API might not be available
    }
  }, [preview]);

  const wordCountColor = useMemo(() => {
    if (annotationWordCount === 0) {
      return "text-muted-foreground";
    }
    if (annotationWordCount >= 150 && annotationWordCount <= 200) {
      return "text-green-600 dark:text-green-400";
    }
    if (annotationWordCount > 200) {
      return "text-amber-600 dark:text-amber-400";
    }
    return "text-muted-foreground";
  }, [annotationWordCount]);

  return (
    <>
      {/* Style selector */}
      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="ab-style"
        >
          {strings.citationStyle}
        </label>
        <select
          className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="ab-style"
          onChange={(e) => setStyle(e.target.value as Style)}
          value={style}
        >
          <option value="apa">{strings.apa7thEdition}</option>
          <option value="mla">{strings.mla9thEdition}</option>
        </select>
      </section>
      {/* Citation input */}
      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="ab-citation"
        >
          {strings.citationReferenceEntry}
        </label>
        <textarea
          className="min-h-[80px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="ab-citation"
          onChange={(e) => setCitation(e.target.value)}
          placeholder={
            style === "apa"
              ? strings.apaCitationPlaceholder
              : strings.mlaCitationPlaceholder
          }
          value={citation}
        />
        <p className="text-muted-foreground text-xs">
          {renderSlots(strings.pasteOrTypeEntry, {
            link: (
              <Link
                className="underline underline-offset-2 hover:text-foreground"
                href="/"
              >
                {strings.citationGenerator}
              </Link>
            ),
          })}
        </p>
      </section>
      {/* Annotation sections */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.annotation}
          </h2>
          <p className={`text-sm ${wordCountColor}`}>
            {t(strings.annotationRange, { count: annotationWordCount })}
          </p>
        </div>

        {/* Summary */}
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ab-summary"
          >
            {strings.summary}
          </label>
          <p className="text-muted-foreground text-xs">
            {strings.whatIsTheSourceAbout}
          </p>
          <textarea
            className="min-h-[100px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="ab-summary"
            onChange={(e) => setSummary(e.target.value)}
            placeholder={strings.thisArticleExamines}
            value={summary}
          />
          <p className="text-right text-muted-foreground text-xs">
            {tPlural(strings.wordsCount, locale, countWords(summary))}
          </p>
        </div>

        {/* Assessment */}
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ab-assessment"
          >
            {strings.assessment}
          </label>
          <p className="text-muted-foreground text-xs">
            {strings.isTheSourceReliableIs}
          </p>
          <textarea
            className="min-h-[100px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="ab-assessment"
            onChange={(e) => setAssessment(e.target.value)}
            placeholder={strings.theAuthorIsALeading}
            value={assessment}
          />
          <p className="text-right text-muted-foreground text-xs">
            {tPlural(strings.wordsCount, locale, countWords(assessment))}
          </p>
        </div>

        {/* Reflection */}
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ab-reflection"
          >
            {strings.reflection}
          </label>
          <p className="text-muted-foreground text-xs">
            {strings.howDoesThisSourceRelate}
          </p>
          <textarea
            className="min-h-[100px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="ab-reflection"
            onChange={(e) => setReflection(e.target.value)}
            placeholder={strings.thisSourceWillBeUseful}
            value={reflection}
          />
          <p className="text-right text-muted-foreground text-xs">
            {tPlural(strings.wordsCount, locale, countWords(reflection))}
          </p>
        </div>
      </section>
      {/* Word count guide */}
      <section className="rounded-lg border border-border bg-muted/30 p-5">
        <h3 className="mb-2 font-semibold text-foreground text-sm">
          {strings.lengthGuideline}
        </h3>
        <p className="text-muted-foreground text-sm">
          {renderSlots(strings.lengthGuidelineText, {
            range: <strong>{strings.n150200Words2}</strong>,
            count: (
              <strong className={wordCountColor}>
                {tPlural(strings.wordsCount, locale, annotationWordCount)}
              </strong>
            ),
          })}
        </p>
        {/* Progress bar */}
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              annotationWordCount <= 200 ? "bg-primary" : "bg-amber-500"
            }`}
            style={{
              width: `${Math.min((annotationWordCount / 200) * 100, 100)}%`,
            }}
          />
        </div>
      </section>
      {/* Preview */}
      {(citation.trim() || annotation) && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-sm">
              {strings.formattedPreview}
            </h2>
            <button
              className="rounded-md border border-border bg-card px-3 py-1.5 text-foreground text-xs transition-colors hover:bg-accent"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copy}
            </button>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <div
              className="text-foreground text-sm leading-relaxed"
              style={{ textIndent: "-0.5in", paddingLeft: "0.5in" }}
            >
              <p className="font-medium">
                {citation.trim() || strings.yourCitationHere}
              </p>
            </div>
            <div
              className="mt-3 text-foreground text-sm leading-relaxed"
              style={{ paddingLeft: "0.5in" }}
            >
              <p>{annotation || strings.yourAnnotationHere}</p>
            </div>
          </div>
          <p className="text-muted-foreground text-xs">
            {style === "apa"
              ? strings.apaHangingIndentOnThe
              : strings.mlaHangingIndentOnThe}
          </p>
        </section>
      )}
      {/* Related tools */}
    </>
  );
}
